import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons'

import classNames from 'classnames'
import { useSelector } from 'react-redux'
import { CourseStateType, EmailTypes } from 'enum'
import CommonToolTip from 'components/ui/tooltip/commonToolTip'
import { LargeSwitch } from 'components/ui/LargeSwitch'
import { Notification } from 'models/course.model'
import { IMainState } from 'store/states'
import InputLabel from '../../../field/input-label/InputLabel'
import ConfirmationModal from '../../../../modals/confirmation/ConfirmationModal'
import SendEmail from '../../components/send-email/SendEmail'
import { useForm } from '../../context'
import useEmailDefaultValues from '../../hooks/useEmailDefaultValues'

interface Props {
  isNewCourse?: boolean
  invitationsDisabled?: boolean
}

const InvitationPublication: React.FC<Props> = ({ isNewCourse, invitationsDisabled = false }) => {
  const { t } = useTranslation()
  const { values, setValues, setFieldValue } = useForm()
  const lessons = useSelector(
    ({ courses }: IMainState) => courses.course?.lessons || []
  )

  const [showConfirmation, setShowConfirmation] = useState(false)
  const isPublished = values.state?.value === CourseStateType.Published

  useEffect(() => {
    if (values.state?.value === CourseStateType.Published) {
      setShowConfirmation(true)
    }
  }, [values.state])

  const handleCloseConfirmationModal = () => {
    setShowConfirmation(!showConfirmation)
    setValues({ ...values, ['state']: undefined })
  }

  const emailSent = useMemo(() => {
    if (values.invitationNotification) {
      const sent = values.invitationNotification?.sent
      return !!sent
    }
    return false
  }, [values.invitationNotification])

  const disabled = values['hasStarted'] || emailSent

  const checked = !!values.invitationNotification?.active

  const getTexts = useEmailDefaultValues()

  const texts = useMemo(
    () =>
      getTexts({
        emailType: EmailTypes.invitation,
        courseName: values.name,
      }),
    [getTexts, values.name]
  )

  const onChange = (values: Partial<Notification>) => {
    setFieldValue('invitationNotification', {
      active: checked,
      emailBody: texts.content,
      emailSubject: texts.subject,
      emailType: EmailTypes.invitation,
      ...values,
    })
  }

  const defaultValues = useMemo(
    () =>
      values.invitationNotification || {
        emailBody: texts.content,
        emailSubject: texts.subject,
      },
    [values.invitationNotification, texts]
  )

  const showConfirmationModal =
    checked &&
    !values['hasStarted'] &&
    !emailSent &&
    isPublished &&
    lessons.length > 0

  return (
    <div className="d-flex align-items-center">
      <InputLabel
        className={classNames('flex-fill mr-3')}
        label={
          <div>
            {t('administration.courses.new.form.invitationNotification.label')}
            {emailSent && (
              <CommonToolTip
                text={t('administration.courses.tooltips.invitationSent')}
                position="right"
              >
                <FontAwesomeIcon icon={faInfoCircle} className={`ml-2`} />
              </CommonToolTip>
            )}
          </div>
        }
      />
      <SendEmail
        defaultValues={defaultValues}
        canEdit={!disabled && !invitationsDisabled}
        onSave={({ emailBody, emailSubject }) => {
          onChange({
            emailBody,
            emailSubject,
          })
        }}
        isNewCourse={isNewCourse}
        courseName={values['name']}
      />
      <CommonToolTip
        text={t(
          'administration.courses.new.form.invitationNotification.disabledCheckboxTooltip'
        )}
        disabled={!disabled}
        position={'left'}
      >
        <LargeSwitch
          checked={checked}
          checkedChildren={t('administration.courses.new.form.yes')}
          unCheckedChildren={t('administration.courses.new.form.no')}
          onChange={(checked) =>
            onChange({
              active: checked,
            })
          }
          disabled={disabled || invitationsDisabled}
        />
      </CommonToolTip>
      {showConfirmationModal && (
        <ConfirmationModal
          show={showConfirmation}
          title={t(
            'administration.courses.new.form.invitationNotification.confirmationModal.header'
          )}
          acceptButtonText={t(
            'administration.courses.new.form.invitationNotification.confirmationModal.continue'
          )}
          text={t(
            'administration.courses.new.form.invitationNotification.confirmationModal.text'
          )}
          onAccept={() => setShowConfirmation(!showConfirmation)}
          onClose={handleCloseConfirmationModal}
        />
      )}
    </div>
  )
}

export default InvitationPublication
